import React, { useState, useRef, useEffect } from 'react';
import './ChatBot.css';

const formatMessage = (text) => {
  // First handle tables
  text = text.replace(/\|(.+?)\|/g, (match) => {
    const rows = match.split('\n').filter(row => row.trim());
    if (rows.length < 2) return match;

    return `<div class="chat-table"><table>${
      rows.map((row, i) => {
        const cells = row.split('|').filter(cell => cell.trim());
        return `<tr>${
          cells.map(cell => 
            `<${i === 0 ? 'th' : 'td'}>${cell.trim()}</${i === 0 ? 'th' : 'td'}>`
          ).join('')
        }</tr>`;
      }).join('')
    }</table></div>`;
  });

  // Then handle other markdown
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/### (.*?)\n/g, '<h3>$1</h3>')
    .replace(/^\d\. (.*)$/gm, '<div class="numbered-item">$1</div>')
    .replace(/^- (.*)$/gm, '<div class="bullet-item">$1</div>')
    .split('\n').map(line => line.trim() ? `<div>${line}</div>` : '<br/>').join('');
};

const ChatBot = ({ fullPage = false }) => {
  const [messages, setMessages] = useState([
    { text: "Hi there! I'm your Yoga Health Assistant. Ask me anything about yoga poses, health benefits, or wellness tips!", sender: 'bot' }
  ]);
  const [input, setInput] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messageEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const API_KEY = 'AIzaSyCNMwDLTs4C8MmumR3PDrbb6RPSgjR-1fs';
  const API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent';

  // Scroll to the latest message
  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Function to toggle chatbot visibility
  const toggleChatbot = () => {
    setIsVisible(!isVisible);
  };

  // Handle input changes
  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  // Function to send message to the API
  const fetchGeminiResponse = async (prompt) => {
    try {
      const response = await fetch(`${API_URL}?key=${API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: `You are a helpful yoga and health assistant. Provide concise, helpful information about yoga poses, techniques, health benefits, and wellness tips. Format your responses using Markdown: use **bold** for pose names and important terms, use *italics* for emphasis, use bullet points (- ) for lists, use numbered lists (1. ) for step-by-step instructions, and use ### for section headings.

                  Always format your responses in a structured, easy-to-read way with proper Markdown formatting.
                  
                  User query: ${prompt}`
                }
              ]
            }
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1024,
          }
        }),
      });

      const data = await response.json();
      
      // Check if the response has the expected structure
      if (data.candidates && data.candidates[0]?.content?.parts?.[0]?.text) {
        return data.candidates[0].content.parts[0].text;
      } else {
        console.error('Unexpected API response structure:', data);
        return "I'm sorry, I couldn't process that request. Please try again.";
      }
    } catch (error) {
      console.error('Error calling Gemini API:', error);
      return "Sorry, I'm having trouble connecting right now. Please try again later.";
    }
  };

  // Handle sending a message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!input.trim()) return;
    
    // Add user message to the chat
    const userMessage = { text: input, sender: 'user' };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    
    // Show typing indicator
    setIsTyping(true);
    
    // Get response from API
    const response = await fetchGeminiResponse(input);
    
    // Hide typing indicator and add bot response
    setIsTyping(false);
    setMessages((prev) => [...prev, { text: response, sender: 'bot' }]);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // For full page mode
  if (fullPage) {
    return (
      <div className="chatbot-fullpage">
        <div className="chatbot-fullpage-messages" ref={messagesContainerRef}>
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`chatbot-message ${message.sender}-message`}
            >
              {message.sender === 'bot' ? (
                <div dangerouslySetInnerHTML={{ __html: formatMessage(message.text) }} />
              ) : (
                message.text
              )}
            </div>
          ))}
          
          {isTyping && (
            <div className="typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          )}
          
          <div ref={messageEndRef} />
        </div>

        <div className="scroll-to-bottom" onClick={scrollToBottom}>
          ↓
        </div>

        <form onSubmit={handleSendMessage} className="chatbot-fullpage-input-container">
          <input
            type="text"
            className="chatbot-input"
            placeholder="Ask about yoga poses, health tips..."
            value={input}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
          />
          <button type="submit" className="chatbot-send-btn">
            →
          </button>
        </form>
      </div>
    );
  }

  // For popup mode (home page)
  return (
    <>
      {/* Chat trigger button */}
      {!isVisible && (
        <div className="chatbot-trigger" onClick={toggleChatbot}>
          🧘
        </div>
      )}

      {/* Chatbot container */}
      <div className={`chatbot-container ${!isVisible ? 'chatbot-minimized' : ''}`}>
        <div className="chatbot-header">
          <div className="chatbot-title">
            <span className="chatbot-title-icon">🧘</span>
            <span>Yoga Health Assistant</span>
          </div>
          <button className="chatbot-toggle-btn" onClick={toggleChatbot}>
            {isVisible ? '✕' : '▲'}
          </button>
        </div>

        <div className="chatbot-messages" ref={messagesContainerRef}>
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`chatbot-message ${message.sender}-message`}
            >
              {message.sender === 'bot' ? (
                <div dangerouslySetInnerHTML={{ __html: formatMessage(message.text) }} />
              ) : (
                message.text
              )}
            </div>
          ))}
          
          {isTyping && (
            <div className="typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          )}
          
          <div ref={messageEndRef} />
        </div>

        <div className="scroll-to-bottom" onClick={scrollToBottom}>
          ↓
        </div>

        <form onSubmit={handleSendMessage} className="chatbot-input-container">
          <input
            type="text"
            className="chatbot-input"
            placeholder="Ask about yoga poses, health tips..."
            value={input}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
          />
          <button type="submit" className="chatbot-send-btn">
            →
          </button>
        </form>
      </div>
    </>
  );
};

export default ChatBot;